import { useState, useMemo, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Pagination } from "@/components/ui/pagination"
import { api, authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { PlusIcon, PencilIcon, Trash2Icon, SearchIcon, XIcon } from "lucide-react"
import { sanitizeForCode, computeAllocations, computeTrajectoryCode, computeTrajectoryName } from "@cdc-sccr/shared"
import { RestoreIcon } from "@/components/icons/RestoreIcon"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

interface Country  { id: string; name: string; isoCode: string }
interface StateRow { id: string; name: string; countryId: string; countryName: string; countryIsoCode: string }
interface CityRow  { id: string; code?: string | null; name: string; sgtCodes?: string[] | null; type: "city" | "trayectory"; deletedAt?: string | null; stateId: string; stateName: string; countryId: string; countryName: string }

type CityForm = {
  name: string
  code: string
  stateId: string
  type: "city" | "trayectory"
}



async function readApiError(res: Response): Promise<string> {
  const text = await res.text()
  if (!text) return `Error ${res.status}`
  try {
    const parsed = JSON.parse(text) as { error?: string }
    return parsed.error ?? text
  } catch {
    return text
  }
}

const PAGE_SIZE = 25
const EMPTY: CityForm = { name: "", code: "", stateId: "", type: "city" }

export default function CitiesPage() {
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [search,        setSearch]        = useState("")
  const [filterCountry, setFilterCountry] = useState("")
  const [filterState,   setFilterState]   = useState("")
  const [filterType,    setFilterType]    = useState<"" | "city" | "trayectory">("")
  const [filterStatus,  setFilterStatus]  = useState<"active" | "deleted" | "all">("active")
  const [page,          setPage]          = useState(1)
  const [sheetOpen,     setSheetOpen]     = useState(false)
  const [deleteOpen,    setDeleteOpen]    = useState(false)
  const [editing,       setEditing]       = useState<CityRow | null>(null)
  const [deleting,      setDeleting]      = useState<CityRow | null>(null)
  const [form,          setForm]          = useState({ ...EMPTY })
  const [formCountry,   setFormCountry]   = useState("")
  const [formError,     setFormError]     = useState<string | null>(null)
  const [selectedCities, setSelectedCities] = useState<CityRow[]>([])
  const [pickerCountry,  setPickerCountry]  = useState("")
  const [pickerSearch,   setPickerSearch]   = useState("")
  const [sgtCodes,       setSgtCodes]       = useState<string[]>([])
  const [sgtInput,       setSgtInput]       = useState("")
  const [filterSgt,      setFilterSgt]      = useState<"all" | "with" | "without">("all")

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: countries = [] } = useQuery({
    queryKey: ["geo", "countries"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/countries`)
      return r.json() as Promise<Country[]>
    },
  })

  const { data: allStates = [] } = useQuery({
    queryKey: ["geo", "states"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/states`)
      return r.json() as Promise<StateRow[]>
    },
  })

  const { data: allCities = [], isLoading } = useQuery({
    queryKey: ["geo", "cities", "list", filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filterStatus === "deleted") params.set("deleted", "true")
      else if (filterStatus === "active") params.set("deleted", "false")
      const r = await authorizedFetch(
        `${API_URL}/api/geo/cities${params.size ? `?${params}` : ""}`,
      )
      return r.json() as Promise<CityRow[]>
    },
  })

  const { data: pickerCities = [] } = useQuery({
    queryKey: ["geo", "cities", "picker"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/cities?deleted=false`)
      return r.json() as Promise<CityRow[]>
    },
  })


  // ── Filtrado + paginación ──────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    const result = allCities.filter(c => {
      const matchCountry = !filterCountry || c.countryId === filterCountry
      const matchState   = !filterState   || c.stateId   === filterState
      const matchType    = !filterType    || c.type      === filterType
      const matchSearch  = !term ||
        c.name.toLowerCase().includes(term) ||
        (c.code ?? "").toLowerCase().includes(term)
      if (!matchCountry || !matchState || !matchType || !matchSearch) return false
      const hasSgt = (c.sgtCodes?.length ?? 0) > 0
      if (filterSgt === "with"    && !hasSgt) return false
      if (filterSgt === "without" &&  hasSgt) return false
      return true
    })
    if (filterSgt === "with") {
      return [...result].sort((a, b) =>
        (a.sgtCodes![0] ?? "").localeCompare(b.sgtCodes![0] ?? "")
      )
    }
    return result
  }, [allCities, filterCountry, filterState, filterType, filterSgt, search])

  useEffect(() => { setPage(1) }, [filterCountry, filterState, filterType, filterStatus, filterSgt, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // Provincias disponibles según el filtro de país
  const filteredStates = filterCountry
    ? allStates.filter(s => s.countryId === filterCountry)
    : allStates

  // Provincias disponibles en el formulario
  const formStates = formCountry
    ? allStates.filter(s => s.countryId === formCountry)
    : allStates

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: async (data: typeof EMPTY) => {
      const payload = {
        name:     data.name.trim(),
        code:     data.code.trim().toUpperCase(),
        stateId:  data.stateId,
        type:     data.type,
        sgtCodes: sgtCodes.length ? sgtCodes : null,
      }
      if (!payload.code) throw new Error("El código es obligatorio")
      const res = await api.geo.cities.$post({ json: payload })
      if (!res.ok) throw new Error(await readApiError(res))
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "cities"] }); closeSheet() },
    onError: (e: Error) => setFormError(e.message),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof EMPTY }) => {
      const payload = {
        name:     data.name.trim(),
        code:     data.code.trim().toUpperCase(),
        stateId:  data.stateId,
        type:     data.type,
        sgtCodes: sgtCodes.length ? sgtCodes : null,
      }
      if (!payload.code) throw new Error("El código es obligatorio")
      const res = await api.geo.cities[":id"].$put({ param: { id }, json: payload })
      if (!res.ok) throw new Error(await readApiError(res))
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "cities"] }); closeSheet() },
    onError: (e: Error) => setFormError(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.geo.cities[":id"].$delete({ param: { id } })
      if (!res.ok) throw new Error(await readApiError(res))
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "cities"] }); setDeleteOpen(false); setDeleting(null) },
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      await authorizedFetch(`${API_URL}/api/geo/cities/${id}/restore`, {
        method: "PATCH",
      })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "cities"] }) },
  })

  // ── Handlers ───────────────────────────────────────────────────────────────
  function closeSheet() {
    setSheetOpen(false); setEditing(null)
    setForm({ ...EMPTY }); setFormCountry(""); setFormError(null)
    setSelectedCities([]); setPickerCountry(""); setPickerSearch("")
    setSgtCodes([]); setSgtInput("")
  }

  function openCreate() {
    setEditing(null)
    setFormCountry(filterCountry)
    setSelectedCities([])
    setPickerCountry(""); setPickerSearch("")
    setSgtCodes([]); setSgtInput("")
    setForm({ name: "", code: "", stateId: filterState || "", type: "city" })
    setFormError(null); setSheetOpen(true)
  }

  function openEdit(city: CityRow) {
    setEditing(city)
    setFormCountry(city.countryId)
    setSelectedCities([])
    setPickerCountry(""); setPickerSearch("")
    setSgtCodes(city.sgtCodes ?? []); setSgtInput("")
    setForm({ name: city.name, code: city.code ?? "", stateId: city.stateId, type: city.type })
    setFormError(null); setSheetOpen(true)
  }

  function openCreateTrajectory() {
    setEditing(null)
    setFormCountry("")
    setSelectedCities([])
    setPickerCountry(filterCountry || "")
    setPickerSearch("")
    setSgtCodes([]); setSgtInput("")
    setForm({ name: "", code: "", stateId: "", type: "trayectory" })
    setFormError(null); setSheetOpen(true)
  }

  function handleCountryFilter(v: string | null) {
    setFilterCountry(v ?? ""); setFilterState("")
  }

  const isEditing = Boolean(editing)
  const isTrajectory = form.type === "trayectory"
  const isTrajectoryCreation = !isEditing && isTrajectory

  useEffect(() => {
    if (!isTrajectoryCreation) return
    const name = computeTrajectoryName(selectedCities)
    const code = computeTrajectoryCode(selectedCities)
    const primaryState = selectedCities[0]?.stateId ?? ""
    setForm((prev) => {
      if (prev.name === name && prev.code === code && prev.stateId === primaryState) return prev
      return { ...prev, name, code, stateId: primaryState }
    })
  }, [selectedCities, isTrajectoryCreation])

  function addSelectedCity(city?: CityRow) {
    const target = city ?? availablePickerCities[0]
    if (!target) return
    if (selectedCities.length >= 10) return
    if (target.type !== "city") {
      setFormError("Seleccioná una ciudad válida")
      return
    }
    if (selectedCities.some((c) => c.id === target.id)) {
      setFormError("Ya agregaste esa ciudad")
      return
    }
    setSelectedCities((prev) => [...prev, target])
    setFormError(null)
  }

  function removeSelectedCity(id: string) {
    setSelectedCities((prev) => prev.filter((c) => c.id !== id))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setFormError(null)

    if (isTrajectoryCreation) {
      if (selectedCities.length < 2) {
        setFormError("Seleccioná al menos dos ciudades para la trayectoria")
        return
      }
      const name = computeTrajectoryName(selectedCities)
      const code = computeTrajectoryCode(selectedCities)
      if (!code) {
        setFormError("No se pudo construir un código válido con las ciudades seleccionadas")
        return
      }
      const stateId = selectedCities[0]?.stateId
      if (!stateId) {
        setFormError("No se pudo determinar la provincia de la primera ciudad")
        return
      }
      const payload = {
        name,
        code,
        stateId,
        type: "trayectory" as const,
      }
      createMut.mutate(payload)
      return
    }

    const payload = {
      ...form,
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
    }
    if (!payload.code) {
      setFormError("El código logístico es obligatorio")
      return
    }
    if (isEditing) updateMut.mutate({ id: editing!.id, data: payload })
    else createMut.mutate(payload)
  }

  const isSaving = createMut.isPending || updateMut.isPending

  // Nombres para los triggers de Select (evita mostrar UUIDs)
  const selCountryName  = countries.find(c => c.id === filterCountry)?.name
  const selStateName    = allStates.find(s => s.id === filterState)?.name
  const formCountryName = countries.find(c => c.id === formCountry)?.name
  const formStateName   = allStates.find(s => s.id === form.stateId)?.name

  const pickerCountryName = countries.find((c) => c.id === pickerCountry)?.name
  const availablePickerCities = useMemo(() => {
    const term = pickerSearch.trim().toLowerCase()
    return pickerCities
      .filter((c) => c.type === "city")
      .filter((c) => !pickerCountry || c.countryId === pickerCountry)
      .filter((c) =>
        !term ||
        c.name.toLowerCase().includes(term) ||
        (c.code ?? "").toLowerCase().includes(term)
      )
      .slice(0, 20)
  }, [pickerCities, pickerCountry, pickerSearch])

  return (
    <>
      <SiteHeader title="Ciudades y Trayectorias" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">

        {/* Barra de herramientas */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {/* Buscador */}
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar por nombre o código..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Filtro país */}
          <Select value={filterCountry} onValueChange={handleCountryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Todos los países">
                {selCountryName ?? (filterCountry ? filterCountry : null)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Todos los países</SelectItem>
              {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Filtro provincia */}
          <Select value={filterState} onValueChange={v => setFilterState(v ?? "")} disabled={!filterCountry}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Todas las provincias">
                {selStateName ?? (filterState ? filterState : null)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Todas las provincias</SelectItem>
              {filteredStates.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Filtro tipo */}
          <Select value={filterType} onValueChange={v => setFilterType((v ?? "") as "" | "city" | "trayectory") }>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Todos los tipos">
                {filterType === "city"
                  ? "Ciudad"
                  : filterType === "trayectory"
                    ? "Trayectoria"
                    : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Todos los tipos</SelectItem>
              <SelectItem value="city">Ciudad</SelectItem>
              <SelectItem value="trayectory">Trayectoria</SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro SGT */}
          <Select value={filterSgt} onValueChange={(v) => setFilterSgt(v as "all" | "with" | "without")}>
            <SelectTrigger className="w-36">
              <SelectValue>
                {filterSgt === "all" ? "Todos (SGT)" : filterSgt === "with" ? "Con SGT" : "Sin SGT"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos (SGT)</SelectItem>
              <SelectItem value="with">Con SGT</SelectItem>
              <SelectItem value="without">Sin SGT</SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro estado */}
          <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v as "active" | "deleted" | "all"); setPage(1) }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="deleted">Borrados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>

          {can("write", "geo") && (
            <div className="flex gap-2">
              <Button onClick={openCreate} className="gap-2 shrink-0">
                <PlusIcon className="size-4" /> Nueva ciudad
              </Button>
              <Button onClick={openCreateTrajectory} variant="secondary" className="gap-2 shrink-0">
                <PlusIcon className="size-4" /> Crear trayectoria
              </Button>
            </div>
          )}
        </div>

        {/* Tabla */}
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Código</TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead className="w-44">Códigos SGT</TableHead>
                <TableHead className="w-36">Tipo</TableHead>
                <TableHead>Provincia</TableHead>
                <TableHead>País</TableHead>
                {can("write", "geo") && <TableHead className="text-right pr-4 w-24">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-16 text-muted-foreground">Cargando...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-16 text-muted-foreground">
                  {search || filterCountry || filterState ? "Sin resultados para la búsqueda" : "No hay ciudades registradas"}
                </TableCell></TableRow>
              ) : paged.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    {c.code
                      ? <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{c.code}</span>
                      : <span className="text-muted-foreground/40 text-xs">—</span>}
                  </TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>
                    {c.sgtCodes?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {c.sgtCodes.slice(0, 2).map((s) => (
                          <span key={s} className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{s}</span>
                        ))}
                        {c.sgtCodes.length > 2 && (
                          <span className="text-xs text-muted-foreground">+{c.sgtCodes.length - 2}</span>
                        )}
                      </div>
                    ) : <span className="text-muted-foreground/40 text-xs">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={c.type === "trayectory"
                        ? "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"
                        : "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200"}
                    >
                      {c.type === "trayectory" ? "Trayectoria" : "Ciudad"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{c.stateName}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{c.countryName}</TableCell>
                  {can("write", "geo") && (
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        {c.deletedAt ? (
                          <Button variant="ghost" size="icon-sm"
                            onClick={() => restoreMut.mutate(c.id)}
                            disabled={restoreMut.isPending}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Restaurar">
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon-sm" onClick={() => openEdit(c)} title="Editar">
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon-sm"
                              onClick={() => { setDeleting(c); setDeleteOpen(true) }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10" title="Eliminar">
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* Sheet: Crear / Editar */}
      <Sheet open={sheetOpen} onOpenChange={(o) => { if (!o) closeSheet(); else setSheetOpen(true) }}>
        <SheetContent side="right" className="sm:max-w-sm">
          <SheetHeader>
            <SheetTitle>{editing ? (editing.type === "trayectory" ? "Editar trayectoria" : "Editar ciudad") : (form.type === "trayectory" ? "Nueva trayectoria" : "Nueva ciudad")}</SheetTitle>
            <SheetDescription>
              {form.type === "trayectory"
                ? isEditing
                  ? "Podés ajustar la información de la trayectoria."
                  : "Seleccioná al menos dos ciudades para construir la trayectoria."
                : editing
                  ? "Editá la información de la ciudad."
                  : "Crear una ciudad logística."}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4">
            <div className="grid gap-1.5">
              <Label htmlFor="ci-name">Nombre</Label>
              <Input
                id="ci-name"
                value={form.name}
                readOnly={isTrajectoryCreation}
                className={isTrajectoryCreation ? "bg-muted/80 text-muted-foreground" : undefined}
                onBlur={e => !isTrajectoryCreation && setForm(f => ({ ...f, name: e.target.value.trim() }))}
                onChange={e => !isTrajectoryCreation && setForm(f => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ci-code">Código logístico</Label>
              <Input
                id="ci-code"
                value={form.code}
                className="font-mono uppercase"
                placeholder={isTrajectory ? "Ej. BSAS/STAFE" : "Ej. BSAS, SP, MDQ"}
                maxLength={10}
                required
                readOnly={isTrajectoryCreation}
                onBlur={e => !isTrajectoryCreation && setForm(f => ({ ...f, code: e.target.value.trim().slice(0, 10) }))}
                onChange={e => {
                  if (isTrajectoryCreation) return
                  const next = e.target.value.replace(/[^A-Za-z0-9/ ]/g, "").toUpperCase()
                  setForm(f => ({ ...f, code: next }))
                }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Tipo</Label>
              <Input value={form.type === "trayectory" ? "Trayectoria" : "Ciudad"} readOnly className="bg-muted/80 text-muted-foreground" tabIndex={-1} />
            </div>

            {isTrajectoryCreation ? (
              <div className="space-y-3">
                <div className="flex flex-col gap-2">
                  <Label>Agregar ciudades (mínimo 2, máximo 10)</Label>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap gap-2">
                      <Select value={pickerCountry} onValueChange={v => { setPickerCountry(v ?? ""); }}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="País">
                            {pickerCountryName ?? (pickerCountry ? pickerCountry : null)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Todos los países</SelectItem>
                          {countries.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={pickerSearch}
                        onChange={e => setPickerSearch(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            e.preventDefault()
                            addSelectedCity()
                          }
                        }}
                        placeholder="Buscar ciudad..."
                        className="w-64"
                      />
                      <Button type="button" onClick={() => addSelectedCity()} disabled={availablePickerCities.length === 0 || selectedCities.length >= 10}>
                        <PlusIcon className="size-4" />
                      </Button>
                    </div>
                    <div className="max-h-48 w-full overflow-y-auto rounded border bg-background">
                      {availablePickerCities.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-muted-foreground">
                          {pickerSearch ? "Sin resultados" : "Usá el buscador para encontrar una ciudad"}
                        </p>
                      ) : (
                        <ul className="divide-y">
                          {availablePickerCities.map(city => (
                            <li key={city.id}>
                              <button
                                type="button"
                                className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
                                onClick={() => addSelectedCity(city)}
                                disabled={selectedCities.some(c => c.id === city.id)}
                              >
                                <span>
                                  <span className="font-medium">{city.name}</span>
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    {countries.find(c => c.id === city.countryId)?.isoCode ?? ""}
                                    {city.code ? ` · ${city.code}` : ""}
                                  </span>
                                </span>
                                <PlusIcon className="size-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
                {selectedCities.length > 0 && (
                  <div className="space-y-2">
                    <Label>Ciudades seleccionadas ({selectedCities.length})</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {selectedCities.map((city, index) => (
                        <div key={city.id} className="flex items-center justify-between rounded border px-3 py-2 text-sm">
                          <div className="flex flex-col">
                            <span className="font-medium">{index + 1}. {city.name}</span>
                            <span className="text-xs text-muted-foreground">{countries.find(c => c.id === city.countryId)?.name ?? ""}</span>
                          </div>
                          <Button type="button" variant="ghost" size="icon-sm" onClick={() => removeSelectedCity(city.id)}>
                            <XIcon className="size-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="grid gap-1.5">
                  <Label>País</Label>
                  <Select value={formCountry} onValueChange={v => { setFormCountry(v ?? ""); setForm(f => ({ ...f, stateId: "" })) }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccioná un país">
                        {formCountryName ?? (formCountry ? formCountry : null)}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ci-state">Provincia</Label>
                  <Select value={form.stateId} onValueChange={v => setForm(f => ({ ...f, stateId: v ?? "" }))} disabled={!formCountry}>
                    <SelectTrigger id="ci-state">
                      <SelectValue placeholder="Seleccioná una provincia">
                        {formStateName ?? (form.stateId ? form.stateId : null)}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {formStates.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {/* Códigos SGT */}
            <div className="grid gap-1.5">
              <Label>Códigos SGT</Label>
              <div className="flex flex-wrap gap-1.5 items-center min-h-9 rounded-md border border-input px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
                {sgtCodes.map((code, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-muted font-mono text-xs px-2 py-0.5 rounded-sm">
                    {code}
                    <button type="button" onClick={() => setSgtCodes(sgtCodes.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground">
                      <XIcon className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={sgtInput}
                  onChange={(e) => setSgtInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault()
                      const val = sgtInput.toUpperCase().trim()
                      if (val && !sgtCodes.includes(val)) setSgtCodes([...sgtCodes, val])
                      setSgtInput("")
                    }
                  }}
                  placeholder={sgtCodes.length === 0 ? "Escribir y Enter para agregar..." : ""}
                  className="flex-1 min-w-28 bg-transparent text-xs font-mono outline-none placeholder:text-muted-foreground"
                />
              </div>
              <p className="text-xs text-muted-foreground">Nombres exactos en SmartGT. Presioná Enter o coma para agregar.</p>
            </div>

            {formError && <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>}
            <SheetFooter className="px-0 mt-2">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button
                type="submit"
                disabled={isSaving || (!isTrajectoryCreation && !form.stateId) || (isTrajectoryCreation && selectedCities.length < 2)}
              >
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : form.type === "trayectory" ? "Crear trayectoria" : "Crear ciudad"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Dialog: Confirmar eliminación */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar ciudad</DialogTitle>
            <DialogDescription>
              ¿Eliminás <strong>{deleting?.name}</strong>? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose><Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button></DialogClose>
            <Button variant="destructive" disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}>
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
